/**
 * Chassis-page Inventory tab — the primary working surface.
 *
 * Renders the v2 port grid (PON or VDSL by adapter_key) and the
 * Run-a-job dropdown. Submitting a job hands it off to the
 * InlineJobTracker (mounted at the foot of this tab, sticky while
 * any tracked job is active) via the ``inline-job-tracker.add``
 * custom DOM event.
 */
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2, Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../../../../api/client";
import { Badge } from "../../../components/Badge";
import { FilterBar } from "../../../components/FilterBar";
import { useToast } from "../../../components/Toast";
import {
  coverageByJob,
  type ScheduleLike,
} from "../../../lib/scheduleCoverage";
import { InlineJobTracker } from "../InlineJobTracker";
import { PonPortsCard } from "../ports/PonPortsCard";
import { VdslPortsCard } from "../ports/VdslPortsCard";

type JobOption = {
  name: string;
  label: string;
  description: string;
};

const GPON_JOB_OPTIONS: JobOption[] = [
  { name: "collect_optical", label: "Collect optical",
    description: "Per-ONU rx/tx power, temperature, voltage." },
  { name: "list_onus", label: "List ONUs",
    description: "Snapshot every ONU (admin/oper state, serial)." },
  { name: "list_pending_onus", label: "List pending ONUs",
    description: "Unprovisioned ONUs visible on the PONs." },
  { name: "parse_alarms", label: "Fetch alarms",
    description: "Active alarms — feeds the Alarms tab + bell badge." },
  { name: "collect_mac", label: "Collect MAC table",
    description: "Per-ONU MAC scan; lands in mac_collection." },
  { name: "backup_running_config", label: "Backup running-config",
    description: "Snapshot config; surface on the Backups tab." },
  { name: "mikrotik_pppoe_sync", label: "Sync PPPoE (MikroTik)",
    description: "Pull active PPPoE sessions from the location's core router." },
];

const HUAWEI_VDSL_JOB_OPTIONS: JobOption[] = [
  { name: "list_ports", label: "List ports",
    description: "Snapshot every VDSL port; lands in vdsl_inventory." },
  { name: "collect_line_speed", label: "Collect line speed",
    description: "Per-port SNR margin, attenuation, max/actual rates." },
  { name: "collect_mac", label: "Collect MAC table",
    description: "Subscriber MACs (vdl rows from display mac-address all)." },
  { name: "parse_alarms", label: "Fetch alarms",
    description: "Active alarms — feeds the Alarms tab + bell badge." },
  { name: "backup_running_config", label: "Backup running-config",
    description: "Snapshot display current-configuration." },
  { name: "mikrotik_pppoe_sync", label: "Sync PPPoE (MikroTik)",
    description: "Pull active PPPoE sessions from the location's core router." },
];

// Ubiquiti UFiber is read-only HTTP. The adapter only declares
// list_onus + mac_table capabilities; the backend rejects any
// other job_name with a capability-mismatch 400. Mirror that
// here so operators don't see "Failed to queue job" after
// clicking Optical / Alarms / Backup on a Ubiquiti chassis.
const UBIQUITI_GPON_JOB_OPTIONS: JobOption[] = [
  { name: "list_onus", label: "List ONUs",
    description: "Snapshot every ONU via UFiber HTTP API." },
  { name: "collect_optical", label: "Collect optical",
    description: "Per-ONU rx/tx power + voltage; one HTTP batch call." },
  { name: "collect_mac", label: "Collect MAC table",
    description: "MAC ↔ ONU serial join from /tools/mac-table." },
];

// Calix E7 is read-only SSH. Same {list_onus, mac_table, optical}
// capability set as Ubiquiti — optical readings ride along in
// `show ont detail` so one SSH call covers inventory + optical.
const CALIX_GPON_JOB_OPTIONS: JobOption[] = [
  { name: "list_onus", label: "List ONUs",
    description: "Snapshot every ONT via `show ont detail`." },
  { name: "collect_optical", label: "Collect optical",
    description: "Per-ONT rx/tx power; reuses the show ont detail output." },
  { name: "collect_mac", label: "Collect MAC table",
    description: "Customer MACs ↔ PON port from `show mac`." },
];

function optionsForAdapter(adapterKey: string): JobOption[] {
  if (adapterKey === "huawei:vdsl") return HUAWEI_VDSL_JOB_OPTIONS;
  if (adapterKey === "ubiquiti:gpon") return UBIQUITI_GPON_JOB_OPTIONS;
  if (adapterKey === "calix:gpon") return CALIX_GPON_JOB_OPTIONS;
  return GPON_JOB_OPTIONS;
}

export function InventoryTab({
  chassisId,
  adapterKey,
}: {
  chassisId: string;
  adapterKey: string;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const options = useMemo(() => optionsForAdapter(adapterKey), [adapterKey]);

  // Which of these jobs the scheduler already runs. Deliberately the
  // FLEET list, not GET /chassis/{id}/schedules — the per-chassis
  // endpoint filters out chassis_id IS NULL rows, so a job covered by a
  // fleet-wide schedule would wrongly read as manual-only.
  const schedules = useQuery<{ schedules: ScheduleLike[] }>({
    queryKey: ["v2", "schedules", "all"],
    queryFn: async () => {
      const res = await api.get<{ schedules: ScheduleLike[] }>("/schedules");
      return res.data;
    },
    staleTime: 60_000,
  });
  const coverage = useMemo(
    () => coverageByJob(schedules.data?.schedules, Number(chassisId)),
    [schedules.data, chassisId],
  );

  // v0.14.1 — track in-flight jobs by name → most-recent jobId, so each
  // dropdown option can render its own spinner until the job hits a
  // terminal state. Map (not Set) lets us poll the specific job's
  // /jobs/<id> endpoint to know when to clear the spinner.
  const [inFlight, setInFlight] = useState<Record<string, string>>({});

  const enqueue = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassisId}/jobs`,
        { name, params: {} },
      );
      return { id: res.data.id, name };
    },
    onSuccess: ({ id, name }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: id, kind: name },
        }),
      );
      toast.push(`${name} queued`, "success");
      // Don't close the dropdown — operator's likely to queue more
      // jobs or want to watch the spinner on the one they just hit.
      // The in-flight Map gates the spinner display.
      setInFlight((prev) => ({ ...prev, [name]: id }));
    },
    onError: () => {
      toast.push("Failed to queue job", "error");
    },
  });

  const onRun = useCallback(
    (name: string) => () => enqueue.mutate(name),
    [enqueue],
  );

  // Poll each in-flight job until terminal. useQueries gives us one
  // query per pending jobId; when one resolves to a terminal status
  // we drop its name from the in-flight map (effect below).
  const TERMINAL = useMemo(
    () => new Set(["succeeded", "failed", "partial", "cancelled"]),
    [],
  );
  const inFlightEntries = useMemo(
    () => Object.entries(inFlight),
    [inFlight],
  );
  const jobQueries = useQueries({
    queries: inFlightEntries.map(([, jobId]) => ({
      queryKey: ["v2", "inventory-tab", "job-poll", jobId],
      queryFn: async () => {
        const res = await api.get<{ id: string; status: string }>(
          `/jobs/${jobId}`,
        );
        return res.data;
      },
      refetchInterval: (q: { state: { data: { status: string } | undefined } }) =>
        q.state.data && TERMINAL.has(q.state.data.status) ? false : 2000,
    })),
  });
  useEffect(() => {
    inFlightEntries.forEach(([name, jobId], idx) => {
      const q = jobQueries[idx];
      const status = q?.data?.status;
      if (status && TERMINAL.has(status)) {
        setInFlight((prev) => {
          // Only remove if it's still the same jobId (operator may
          // have re-triggered the same job during the poll cycle).
          if (prev[name] !== jobId) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    });
  }, [inFlightEntries, jobQueries, TERMINAL]);

  return (
    <div className="flex flex-col gap-3">
      {/* Run-a-job dropdown */}
      <section className="rounded-lg bg-slate-900 shadow-border p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Run a job
          </h2>
          <button
            type="button"
            onClick={() => setOpen((p) => !p)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            <Play className="h-3 w-3" />
            {open ? "Hide jobs" : `Browse ${options.length} jobs`}
            <ChevronDown
              className={[
                "h-3 w-3 transition-transform",
                open ? "rotate-180" : "",
              ].join(" ")}
            />
          </button>
        </div>
        {open && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {options.map((opt) => {
              const running = inFlight[opt.name];
              const auto = coverage.get(opt.name);
              return (
                <button
                  key={opt.name}
                  type="button"
                  onClick={onRun(opt.name)}
                  disabled={enqueue.isPending}
                  className="group flex flex-col gap-1 rounded border border-slate-800 bg-slate-950/40 p-2 text-left hover:border-emerald-700 hover:bg-emerald-950/20 disabled:opacity-50"
                >
                  <span className="inline-flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-200 group-hover:text-emerald-200">
                    {opt.label}
                    {running ? (
                      <Loader2
                        className="h-3 w-3 animate-spin text-emerald-400"
                        aria-label="job in flight"
                      />
                    ) : null}
                    {auto && (
                      <Badge
                        tone="neutral"
                        title={
                          `Runs automatically — ${auto.scope === "fleet" ? "fleet-wide" : "this chassis"}` +
                          `, ${auto.cadence}.` +
                          (auto.nextRunAt
                            ? ` Next: ${new Date(auto.nextRunAt).toLocaleString()}.`
                            : "") +
                          " Running it now is still fine — it just may be redundant."
                        }
                      >
                        auto · {auto.cadence}
                      </Badge>
                    )}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {opt.description}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Port grid (adapter-specific) */}
      {adapterKey === "huawei:vdsl" ? (
        <VdslPortsCard chassisId={chassisId} />
      ) : (
        <>
          <FilterBar
            value={filter}
            onChange={setFilter}
            placeholder="Filter by serial, display ID, MAC, state…"
            className="w-full sm:w-96"
          />
          {/* Row ⋮ actions deep-link into Provisioning → Manual ops,
              which read-only adapters don't have. */}
          <PonPortsCard
            chassisId={chassisId}
            filter={filter}
            rowActions={
              adapterKey !== "ubiquiti:gpon" && adapterKey !== "calix:gpon"
            }
          />
        </>
      )}

      {/* Sticky inline tracker — only renders when at least one job
          is tracked. The tracker itself handles its own visibility. */}
      <InlineJobTracker />
    </div>
  );
}
